import React, { useCallback } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import { FilterValue, TableFilter } from "../types";

import { ReactComponent as CloseIcon } from "assets/images/icon_close_modal.svg";

import { styles } from "./styles";

interface ActiveFiltersProps<T> {
  activeFilters: T;
  filters: TableFilter<T>[];
  onChange: (filterId: keyof T, newActiveFilters: FilterValue) => void;
  onClear: () => void;
}

const ActiveFilters = <T extends Record<string, FilterValue>>(
  props: ActiveFiltersProps<T>
) => {
  const { activeFilters, filters, onChange, onClear } = props;

  const onRemoveFilter = useCallback(
    (filter: TableFilter<T>, value: string) => {
      const newValues = filter.removeValue(activeFilters[filter.id], value);
      onChange(filter.id, newValues);
    },
    [onChange, activeFilters]
  );

  const chips = filters.flatMap((filter) => {
    const values = filter.extractValues(activeFilters[filter.id]);

    if (values.length === 0) {
      return [];
    }

    return values.map((value) => (
      <Chip
        key={`${String(filter.id)}-${value}`}
        sx={styles.chip}
        label={
          <Typography variant="subtitle2">
            {filter.label}: {value}
          </Typography>
        }
        onClick={() => onRemoveFilter(filter, value)}
        onDelete={() => onRemoveFilter(filter, value)}
        deleteIcon={<CloseIcon />}
      />
    ));
  });

  return chips.length > 0 ? (
    <>
      <Divider sx={{ my: 1, backgroundColor: "#D9D9D9" }} />
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {chips}
        <Chip
          data-testid="clear-filters"
          sx={styles.chip}
          label={<Typography variant="subtitle2">Очистити все</Typography>}
          onClick={onClear}
        />
      </Box>
    </>
  ) : null;
};

export default React.memo(ActiveFilters) as typeof ActiveFilters;
